import React from "react";
import { Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Settings as SettingsIcon, LogOut, Sun, Moon, Menu } from "lucide-react";
import SideNav from "@/components/layout/SideNav";
import NotificationBell from "@/components/layout/NotificationBell";
import { NotificationsProvider } from "@/context/NotificationsContext";

const AppLayout = () => {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <NotificationsProvider>
    <div className="min-h-screen bg-background flex flex-col">
      <header className="sticky top-0 z-40 w-full backdrop-blur-xl bg-background/80 border-b border-border/60">
        <div className="h-16 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn"><Menu className="w-5 h-5" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72">
                <SheetHeader className="px-6 pt-6"><SheetTitle>PAYD</SheetTitle></SheetHeader>
                <SideNav role={user?.role} />
              </SheetContent>
            </Sheet>
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary grid place-items-center text-primary-foreground font-bold">P</div>
              <span className="font-display text-lg font-bold tracking-tight hidden sm:inline">PAYD</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle-btn">
              {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 pl-2 pr-3 h-9 rounded-full bg-secondary hover:bg-secondary/80 transition-colors" data-testid="user-menu-btn">
                  <div className="w-7 h-7 rounded-full bg-primary grid place-items-center text-primary-foreground text-xs font-bold overflow-hidden">
                    {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : user?.name?.[0]?.toUpperCase()}
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-2">
                  <div className="text-sm font-medium truncate">{user?.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-widest text-primary font-semibold">{user?.role}</div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                  <SettingsIcon className="w-4 h-4 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="w-full flex-1 max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        <aside className="hidden lg:block">
          <div className="sticky top-20 bg-card border border-border rounded-xl">
            <SideNav role={user?.role} />
          </div>
        </aside>
        <main className="min-w-0">
          <Outlet />
        </main>
      </div>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
          <span>© {new Date().getFullYear()} PAYD</span>
          <div className="flex items-center gap-5">
            <Link to="/help" className="hover:text-foreground transition-colors" data-testid="footer-help">Help</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors" data-testid="footer-terms">Terms</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors" data-testid="footer-privacy">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
    </NotificationsProvider>
  );
};

export default AppLayout;
